// What a call to the sign-in service came back with, in the terms the forms speak: one place that turns the
// service's answer (a code, a status, how long to wait) into what happened. A form never decides from the
// text of an error, and a failure to reach the service (no network, a broken response) is only "general".
export type AuthOutcome =
  /**
   * It went well. `pending` is set when the account the call was about still has its email to verify;
   * `emailSent` is false when the account was made but the verification email could not go out; `wait` is the
   * seconds before another email can be asked for.
   */
  | { kind: 'ok'; pending?: boolean; emailSent?: false; wait?: number }
  /** The verification email could not be sent: the account is still pending and it can be tried again. */
  | { kind: 'sendFailed' }
  /** The email or the password is not right (the service does not say which, on purpose). */
  | { kind: 'credentials' }
  | { kind: 'unverified' }
  | { kind: 'exists' }
  /** The service did not accept the new password: the rule it breaks. */
  | { kind: 'password'; rule: 'min' | 'max' }
  /** The link in the email is not valid (used, wrong or expired). */
  | { kind: 'link' }
  /** Too many attempts; `seconds` is the wait the service asked for, null when it did not say. */
  | { kind: 'limit'; seconds: number | null }
  | { kind: 'general' };

interface Failure {
  code?: string;
  status?: number;
}

export function outcomeOf(
  failure: Failure | null | undefined,
  retryAfter: number | null,
  pending = false,
  emailSent = true,
): AuthOutcome {
  if (!failure) {
    const ok: AuthOutcome = { kind: 'ok' };
    if (pending) ok.pending = true;
    if (!emailSent) ok.emailSent = false;
    if (retryAfter !== null && retryAfter > 0) ok.wait = Math.ceil(retryAfter);
    return ok;
  }
  if (failure.status === 429)
    return { kind: 'limit', seconds: retryAfter !== null && retryAfter > 0 ? Math.ceil(retryAfter) : null };
  switch (failure.code) {
    case 'INVALID_EMAIL_OR_PASSWORD':
    // Changing the password of the open session: the current one is not right.
    case 'INVALID_PASSWORD':
      return { kind: 'credentials' };
    case 'EMAIL_NOT_VERIFIED':
      return { kind: 'unverified' };
    case 'USER_ALREADY_EXISTS':
    case 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL':
      return { kind: 'exists' };
    case 'PASSWORD_TOO_SHORT':
      return { kind: 'password', rule: 'min' };
    case 'PASSWORD_TOO_LONG':
      return { kind: 'password', rule: 'max' };
    case 'INVALID_TOKEN':
      return { kind: 'link' };
    case 'EMAIL_SEND_FAILED':
      return { kind: 'sendFailed' };
    default:
      return { kind: 'general' };
  }
}

/** The header the service uses to say how many seconds to wait. */
export function retryAfterOf(headers: { get(name: string): string | null }): number | null {
  const value = headers.get('X-Retry-After') ?? headers.get('Retry-After');
  const seconds = value === null ? Number.NaN : Number(value);
  return Number.isFinite(seconds) ? seconds : null;
}

type FetchOptions = { onResponse: (context: { response: Response }) => void };

/** Runs a call to the service and says what happened; it never throws. */
export async function runAuth(
  call: (fetchOptions: FetchOptions) => Promise<{ error: Failure | null }>,
): Promise<AuthOutcome> {
  let retryAfter: number | null = null;
  let pending = false;
  let emailSent = true;
  try {
    const { error } = await call({
      onResponse: ({ response }) => {
        retryAfter = retryAfterOf(response.headers);
        // Said by the service only to whoever changed the password with a valid link.
        pending = response.headers.get('X-Account-Pending') === 'true';
        // Said by the service when an account was made: whether its verification email went out.
        emailSent = response.headers.get('X-Email-Sent') !== 'false';
      },
    });
    return outcomeOf(error, retryAfter, pending, emailSent);
  } catch {
    return { kind: 'general' };
  }
}

/**
 * The two calls that act on an account whose email is still to verify, behind its password (the account is
 * not signed in): sending the link again and correcting the address. They answer with the same outcomes as
 * the rest: a wrong password is `credentials`, a wait is `limit`, an email that could not go out is
 * `sendFailed`, and a good answer carries the seconds before another email can be asked for. Never throws.
 */
export async function pendingCall(
  action: 'resend' | 'change-email',
  body: { email: string; password: string; newEmail?: string; callbackURL: string },
): Promise<AuthOutcome> {
  try {
    const response = await fetch(`/api/app/pending/${action}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    const code = await response
      .json()
      .then((data: { code?: string }) => data.code)
      .catch(() => undefined);
    return outcomeOf(response.ok ? null : { code, status: response.status }, retryAfterOf(response.headers));
  } catch {
    return { kind: 'general' };
  }
}
